import { FC, useEffect, useRef, useState } from 'react'

import { debounce } from 'utils/debounce'

import useViewport from 'libs/use-viewport'

import cn from 'classnames'

import styles from './styles.module.scss'

export const InputRange: FC<{
  maxValue: number
  minValue: number
  step: number
  value: string
  onChange: (value: number) => void
  mainColor: string
  secondColor: string
  className?: string
  isPinV2?: boolean
}> = ({ maxValue, minValue, step, value, onChange, mainColor, secondColor, className, isPinV2 }) => {
  const _pos = ((+value.toString().replace(/\s/g, '') - minValue) / (maxValue - minValue)) * 100
  const pos = _pos < 0 ? 0 : _pos > 100 ? 100 : _pos // keep in range 0 <= _pos <= 100
  const selectionWidth = useRef<number>(0)
  const selectionLeft = useRef<number>(0)
  const ref = useRef<HTMLInputElement>()
  const startedRef = useRef<boolean>(false)
  const [started, setStarted] = useState<boolean>(false)

  const handleStart = () => {
    updateSelectionSizes()
    startedRef.current = true
    setStarted(true)
  }

  const handleEnd = () => {
    startedRef.current = false
    setStarted(false)
  }

  const computeValue = (pageX: number): number => {
    return (
      Math.round(
        (minValue + ((maxValue - minValue) * (pageX - 6 - selectionLeft.current)) / selectionWidth.current) / step,
      ) * step
    )
  }

  useEffect(() => {
    updateSelectionSizes()
  }, [])

  const handleMove = debounce((pageX) => {
    let newValue = computeValue(pageX)
    if (maxValue < newValue) newValue = maxValue
    else if (newValue < minValue) newValue = minValue

    if (+value !== newValue) {
      onChange(newValue)
    }
  }, 16.6)

  const handleMoveMouse = (e) => {
    if (startedRef.current) {
      e.preventDefault()
      handleMove(e.pageX)
    }
  }

  const handleMoveTouch = (e) => {
    if (startedRef.current) {
      e.preventDefault()
      handleMove(e.targetTouches[0].pageX)
    }
  }

  const handleJump = (e): void => {
    let newValue = computeValue(e.pageX)
    if (maxValue < newValue) newValue = maxValue
    else if (newValue < minValue) newValue = minValue

    if (+value !== newValue) {
      onChange(newValue)
    }
  }

  const updateSelectionSizes = () => {
    if (ref.current) {
      const { width, left } = ref.current.getBoundingClientRect()
      if (width !== selectionWidth.current) selectionWidth.current = width
      if (left !== selectionLeft.current) selectionLeft.current = left
    }
  }

  const [viewport] = useViewport()
  useEffect(() => {
    document.addEventListener('mousemove', handleMoveMouse)
    document.addEventListener('touchmove', handleMoveTouch, { passive: true })
    document.addEventListener('mouseup', handleEnd)
    document.addEventListener('touchend', handleEnd, { passive: true })
    document.addEventListener('resize', updateSelectionSizes)

    return () => {
      document.removeEventListener('mousemove', handleMoveMouse)
      document.removeEventListener('touchmove', handleMoveTouch)
      document.removeEventListener('mouseup', handleEnd)
      document.removeEventListener('touchend', handleEnd)
      document.removeEventListener('resize', updateSelectionSizes)
    }
  }, [viewport])

  return (
    <div ref={ref} className={cn(styles.container, className)}>
      <div
        className={styles.filled}
        style={{
          background: mainColor,
          width: `${pos}%`,
        }}
      />
      <div className={styles.background} onMouseDown={handleJump} onTouchStart={handleStart} />
      <div
        className={cn(styles.pinWrapper, { [styles.pinWrapper_v2]: isPinV2 })}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        style={{
          left: `calc(${pos}% + -6px)`,
          transform: started ? 'scale(1.3)' : 'none',
        }}
      >
        <div
          className={cn(styles.pin, { [styles.pin_v2]: isPinV2 })}
          style={{
            borderColor: mainColor,
            background: secondColor,
          }}
        />
      </div>
    </div>
  )
}
